import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import routes from '@commons/routes';
import { isBetaBadgeVisible } from '@commons/betaBadge';
import { useAppSelector } from '@store';

export interface NavigationLink {
  link: string | (() => void);
  label: string;
  links?: NavigationLink[];
  type?: 'divider' | 'label';
  color?: string;
  /** Short label shown next to the nav text (e.g. “Beta” during rollout). */
  badge?: string;
}

/** Group saved filters by category (same layout as the Filters menu). */
function buildCategoryGroupedFilterLinks(
  filters: any[],
  hrefForFilter: (filterId: string) => string
): NavigationLink[] {
  const viewsByCategory = filters.reduce(
    (acc: Record<string, any[]>, view: any) => {
      const category = view.category || 'Other';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(view);
      return acc;
    },
    {}
  );

  const links: NavigationLink[] = [];
  const categories = Object.keys(viewsByCategory);

  categories.forEach((category, categoryIndex) => {
    links.push({
      type: 'label',
      label: category,
      link: '#',
    });

    viewsByCategory[category].forEach((view: any) => {
      links.push({
        link: hrefForFilter(view.id),
        label: view.name,
      });
    });

    if (categoryIndex < categories.length - 1) {
      links.push({
        type: 'divider',
        link: '#',
        label: '',
      });
    }
  });

  return links;
}

interface UseNavigationLinksProps {
  username: string;
  onGetQueueStats: () => void;
  onGetQueueHealth: () => void;
  onRestartConsumer: () => void;
  onStartMultipleConsumers: () => void;
  onForceReconnect: () => void;
  onResetQueues: () => void;
  onResetLockedFields: () => void;
  onTriggerLoop: () => void;
}

export const useNavigationLinks = ({
  username,
  onGetQueueStats,
  onGetQueueHealth,
  onRestartConsumer,
  onStartMultipleConsumers,
  onForceReconnect,
  onResetQueues,
  onResetLockedFields,
  onTriggerLoop,
}: UseNavigationLinksProps): NavigationLink[] => {
  const intl = useIntl();
  const views = useAppSelector((state) => state.filters.filters);

  return useMemo(() => {
    const viewsLinks = buildCategoryGroupedFilterLinks(views, (id) =>
      routes.routes.filters.view(username, id)
    );

    const queryFoldersLinks = buildCategoryGroupedFilterLinks(views, (id) =>
      routes.routes.queryFolders.view(username, id)
    );

    const queryFoldersMenuLinks: NavigationLink[] =
      queryFoldersLinks.length > 0
        ? queryFoldersLinks
        : [
            {
              type: 'label',
              label: intl.formatMessage({ id: 'queryFoldersEmpty' }),
              link: '#',
            },
          ];

    return [
      {
        link: routes.routes.dashboard(username),
        label: intl.formatMessage({ id: 'dashboard' }),
      },
      {
        link: routes.routes.models(username),
        label: intl.formatMessage({ id: 'models' }),
      },
      {
        link: '#',
        label: intl.formatMessage({ id: 'filters' }),
        links: [
          ...viewsLinks,
          { type: 'divider', link: '#', label: '' },
          {
            link: routes.routes.filters.list(username),
            label: intl.formatMessage({ id: 'all' }),
          },
        ],
      },
      {
        link: '#',
        label: intl.formatMessage({ id: 'queryFolders' }),
        ...(isBetaBadgeVisible()
          ? { badge: intl.formatMessage({ id: 'beta' }) }
          : {}),
        links: queryFoldersMenuLinks,
      },
      {
        link: routes.routes.uploads(username),
        label: intl.formatMessage({ id: 'uploads' }),
      },
      {
        link: '#',
        label: intl.formatMessage({ id: 'tools' }),
        links: [
          {
            type: 'label',
            label: intl.formatMessage({ id: 'monitor' }),
            link: '#',
          },
          {
            link: onGetQueueStats,
            label: intl.formatMessage({ id: 'queue.viewDetailedStatistics' }),
          },
          {
            link: onGetQueueHealth,
            label: intl.formatMessage({ id: 'queue.checkHealthStatus' }),
          },
          { type: 'divider', link: '#', label: '' },
          {
            type: 'label',
            label: intl.formatMessage({ id: 'control' }),
            link: '#',
          },
          {
            link: onRestartConsumer,
            label: intl.formatMessage({ id: 'queue.restartConsumer' }),
          },
          {
            link: onStartMultipleConsumers,
            label: intl.formatMessage({ id: 'queue.startMultipleConsumers' }),
          },
          {
            link: onForceReconnect,
            label: intl.formatMessage({ id: 'queue.forceReconnect' }),
          },
          { type: 'divider', link: '#', label: '' },
          {
            link: onResetQueues,
            label: intl.formatMessage({ id: 'queue.resetQueues' }),
            color: 'red',
          },
          { type: 'divider', link: '#', label: '' },
          {
            link: onResetLockedFields,
            label: intl.formatMessage({ id: 'queue.resetLockedFields' }),
            color: 'orange',
          },
          {
            link: onTriggerLoop,
            label: intl.formatMessage({ id: 'triggerLoop' }),
          },
        ],
      },
      {
        link: routes.routes.settings(username),
        label: intl.formatMessage({ id: 'settings' }),
      },
    ];
  }, [
    username,
    views,
    intl,
    onGetQueueStats,
    onGetQueueHealth,
    onRestartConsumer,
    onStartMultipleConsumers,
    onForceReconnect,
    onResetQueues,
    onResetLockedFields,
    onTriggerLoop,
  ]);
};
